import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

export function savedMessage(address) {
  return address
    ? `Saved. Make sure your forward to ${address} is set up — we'll log in on the next scheduled run.`
    : "Saved. We'll log in on the next scheduled run.";
}

const EMPTY = { username: "", password: "", displayName: "", url: "", description: "" };

/**
 * One form: pick a portal (or "Not listed? Add it anyway"), username, password,
 * optional nickname. Subscribes via POST /catalog/subscribe (then names it with
 * PATCH /catalog/my-portals/{id}) or files a POST /catalog/request-portal.
 * Passwords live only in component state and the request body, never logged.
 */
export default function AddPortalDialog({ open, onOpenChange, intakeEmail, onAdded }) {
  const [catalog, setCatalog] = useState([]);
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState(null);
  const [custom, setCustom] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    api
      .get("/catalog")
      .then((res) => {
        if (alive) setCatalog(res.data?.portals || []);
      })
      .catch(() => {
        if (alive) setCatalog([]);
      });
    return () => {
      alive = false;
    };
  }, [open]);

  const reset = () => {
    setQuery("");
    setChosen(null);
    setCustom(false);
    setForm(EMPTY);
  };

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog
      .filter((p) => !p.subscribed && !p.is_custom)
      .filter((p) => !q || (p.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [catalog, query]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const ready = custom
    ? form.url.trim() && form.description.trim() && form.username && form.password
    : chosen && form.username && form.password;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || submitting) return;
    setSubmitting(true);
    try {
      let addedId = null;
      if (custom) {
        await api.post("/catalog/request-portal", {
          portal_url: form.url.trim(),
          username: form.username,
          password: form.password,
          description: form.description.trim(),
        });
        toast.success("Request sent. We'll set this portal up and let you know.");
      } else {
        const res = await api.post("/catalog/subscribe", {
          catalog_id: chosen.catalog_id,
          username: form.username,
          password: form.password,
        });
        addedId = res.data?.portal_id || null;
        const name = form.displayName.trim();
        if (name && res.data?.portal_id) {
          try {
            await api.patch(`/catalog/my-portals/${res.data.portal_id}`, { display_name: name });
          } catch {
            toast.error("Portal added, but the name could not be saved.");
          }
        }
        toast.success(savedMessage(intakeEmail));
      }
      reset();
      onOpenChange(false);
      onAdded?.(addedId);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not add the portal. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add a portal</DialogTitle>
          <DialogDescription>
            We log in for you and collect your documents. Your password is stored encrypted.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {custom ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="ap-url">Portal website</Label>
                <Input
                  id="ap-url"
                  type="url"
                  placeholder="https://portal.example.com"
                  value={form.url}
                  onChange={set("url")}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ap-desc">What documents does it have?</Label>
                <Input
                  id="ap-desc"
                  maxLength={200}
                  placeholder="e.g. capital calls and statements"
                  value={form.description}
                  onChange={set("description")}
                />
              </div>
              <button
                type="button"
                className="text-xs underline text-gray-500"
                onClick={() => setCustom(false)}
              >
                Back to the list
              </button>
            </>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="ap-search">Portal</Label>
              {chosen ? (
                <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
                  <span data-testid="chosen-portal">{chosen.name}</span>
                  <button
                    type="button"
                    className="text-xs underline text-gray-500"
                    onClick={() => setChosen(null)}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <Input
                    id="ap-search"
                    placeholder="Search portals…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoComplete="off"
                  />
                  <ul className="max-h-40 overflow-y-auto rounded-md border border-gray-100 divide-y divide-gray-100">
                    {matches.map((p) => (
                      <li key={p.catalog_id}>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                          onClick={() => setChosen(p)}
                        >
                          {p.name}
                        </button>
                      </li>
                    ))}
                    {matches.length === 0 && (
                      <li className="px-3 py-2 text-xs text-gray-400">No match.</li>
                    )}
                  </ul>
                </>
              )}
              {!chosen && (
                <button
                  type="button"
                  className="text-xs underline"
                  style={{ color: BRAND.text }}
                  onClick={() => setCustom(true)}
                >
                  Not listed? Add it anyway
                </button>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="ap-user">Username</Label>
            <Input
              id="ap-user"
              autoComplete="off"
              value={form.username}
              onChange={set("username")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ap-pass">Password</Label>
            <Input
              id="ap-pass"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={set("password")}
            />
          </div>
          {!custom && (
            <div className="space-y-1.5">
              <Label htmlFor="ap-name">What do you call it? (optional)</Label>
              <Input
                id="ap-name"
                maxLength={60}
                value={form.displayName}
                onChange={set("displayName")}
              />
            </div>
          )}

          <div className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
            Does this portal email you a login code? Forward those emails to{" "}
            {intakeEmail ? (
              <code className="font-mono text-gray-900">{intakeEmail}</code>
            ) : (
              "your Elementar address (being set up)"
            )}
            .
          </div>

          <Button
            type="submit"
            disabled={!ready || submitting}
            className="w-full text-white"
            style={{ backgroundColor: BRAND.bg }}
          >
            {submitting ? "Adding…" : "Add portal"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
